import { format, parseISO, isValid } from "date-fns";
import {
  formatInstantInTimezone,
  getDisplayTimezone,
  isDateOnlyString,
} from "@/utils/timezone";

export const APP_DATE_FORMAT = "MM/dd/yyyy";

const SUPPORTED_INSTANT_FORMATS: Record<string, Intl.DateTimeFormatOptions> = {
  [APP_DATE_FORMAT]: { month: "2-digit", day: "2-digit", year: "numeric" },
  "MMM dd, yyyy": { month: "short", day: "2-digit", year: "numeric" },
  "MMMM d, yyyy": { month: "long", day: "numeric", year: "numeric" },
};

export function safeDateParse(date: Date | string | null | undefined): Date | null {
  if (!date) return null;
  if (date instanceof Date) return isValid(date) ? date : null;

  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split("-").map(Number);
    const dateObj = new Date(y, m - 1, d);
    if (isValid(dateObj)) return dateObj;
  }

  const parsed = typeof date === "string" ? parseISO(date) : new Date(date);
  return isValid(parsed) ? parsed : null;
}

function formatInstantStringInDisplayTimezone(
  value: string,
  formatStr: string
): string | null {
  const options = SUPPORTED_INSTANT_FORMATS[formatStr];
  if (!options) return null;
  return formatInstantInTimezone(value, getDisplayTimezone(), options, "en-US");
}

export function formatDate(
  date: Date | string | null | undefined,
  fallback: string = "",
  formatStr: string = APP_DATE_FORMAT
): string {
  const dateObj = safeDateParse(date);
  if (!dateObj) return fallback;

  const isCivilDate =
    date instanceof Date ||
    (typeof date === "string" && isDateOnlyString(date));

  if (isCivilDate) {
    return format(dateObj, formatStr);
  }

  if (typeof date === "string") {
    const formatted = formatInstantStringInDisplayTimezone(date, formatStr);
    if (formatted) return formatted;
  }

  return format(dateObj, formatStr);
}
